import isEmpty from 'lodash/isEmpty';
import isObjectLike from 'lodash/isObjectLike';
import reduce from 'lodash/reduce';
import type { PropsWithChildren } from 'react';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { LuFilter } from 'react-icons/lu';
import styled from 'styled-components';

import { Button } from '@/components/ui/button';
import { titleCase } from 'utils';

import { Form } from './form';
import { IconClose } from './icon';
import { HorizontalLayout } from './Layout';
import { ButtonsContainer, Modal, ModalContent } from './Modal';
import { Text } from './Text';

type FilterValue = any;

export interface TableFilters<T> {
    [k: string]: {
        label: string;
        filterFunction: (data: T, filterValue: FilterValue) => boolean;
        valueDecoder?: (filterValue: FilterValue) => string;
    };
}

export const useTableFilter: <T>(
    data: T[] | undefined,
    filters: TableFilters<T>,
    defaultValues?: any,
    activeFilterslocalStorageKey?: string,
) => {
    filteredData: T[];
    FilterButton: (props: PropsWithChildren) => JSX.Element;
    FilterBar: JSX.Element | null;
} = (data, filters, defaultValues = {}, activeFilterslocalStorageKey = '') => {
    const [filterModalOpen, setFilterModalOpen] = useState(false);
    const [activeFilters, setActiveFiltersFilters] = useState<{ [k: string]: FilterValue }>(defaultValues);

    useEffect(() => {
        const stringifiedItem = localStorage.getItem(activeFilterslocalStorageKey);
        if (activeFilterslocalStorageKey && stringifiedItem) {
            setActiveFiltersFilters(JSON.parse(stringifiedItem));
        }
    }, [setActiveFiltersFilters, activeFilterslocalStorageKey]);

    useEffect(() => {
        if (activeFilterslocalStorageKey && activeFilters) {
            localStorage.setItem(activeFilterslocalStorageKey, JSON.stringify(activeFilters));
        }
    }, [activeFilters, activeFilterslocalStorageKey]);

    const onFilterSubmit = useCallback((filterForm: any) => {
        setActiveFiltersFilters(filterForm);
        setFilterModalOpen(false);
    }, []);

    const onFiltersClear = useCallback(() => {
        setActiveFiltersFilters(defaultValues);
        setFilterModalOpen(false);
    }, [defaultValues]);

    const onFilterRemove = useCallback(
        (filterName: string, filterValue: FilterValue) => {
            const currentValue = activeFilters[filterName];
            const newValue = Array.isArray(currentValue)
                ? currentValue.filter((val) => val !== filterValue)
                : defaultValues[filterName];
            setActiveFiltersFilters((state) => ({ ...state, [filterName]: newValue }));
        },
        [activeFilters, defaultValues],
    );

    const activeFilterCount = useMemo(() => {
        const keys = Object.keys(activeFilters);
        return keys.length
            ? keys.reduce(
                  (count: number, key: string) =>
                      count + Number(isFilterEnabled(activeFilters[key]) && activeFilters[key] !== defaultValues[key]),
                  0,
              )
            : 0;
    }, [activeFilters, defaultValues]);

    const FilterButton = ({ children }: PropsWithChildren) => (
        <TableFilter
            activeFilterCount={activeFilterCount}
            openModal={() => setFilterModalOpen(true)}
            closeModal={() => setFilterModalOpen(false)}
            filterModalOpen={filterModalOpen}
            filters={activeFilters}
            onSubmit={onFilterSubmit}
            onClear={onFiltersClear}
        >
            {children}
        </TableFilter>
    );

    const FilterBar = useMemo(() => {
        const activeFilterArray: { filterName: string; filterValue: FilterValue }[] = reduce(
            activeFilters,
            (result, filterValue, filterName) =>
                isFilterEnabled(filterValue) ? [...result, { filterValue, filterName }] : result,
            [] as { filterName: string; filterValue: string }[],
        );

        return activeFilterArray.length > 0 ? (
            <HorizontalLayout $gap={8}>
                {activeFilterArray
                    .filter(({ filterName, filterValue }) => defaultValues[filterName] !== filterValue)
                    .map(({ filterName, filterValue }) => {
                        if (Array.isArray(filterValue)) {
                            return (
                                <React.Fragment key={filterName}>
                                    {filterValue.map((val: FilterValue) => (
                                        <FilterLabel
                                            key={`${filterName}-${val}`}
                                            onRemove={() => onFilterRemove(filterName, val)}
                                            label={`${filters[filterName].label} - ${titleCase(val)}`}
                                            onClick={() => setFilterModalOpen(true)}
                                        />
                                    ))}
                                </React.Fragment>
                            );
                        }

                        const filterDefinition = filters[filterName];
                        return (
                            <FilterLabel
                                key={filterName}
                                onRemove={() => onFilterRemove(filterName, filterValue)}
                                label={
                                    filterDefinition.valueDecoder
                                        ? `${filterDefinition.label} - ${filterDefinition.valueDecoder(filterValue)}`
                                        : filterDefinition.label
                                }
                                onClick={() => setFilterModalOpen(true)}
                            />
                        );
                    })}
            </HorizontalLayout>
        ) : null;
    }, [activeFilters, filters, onFilterRemove, defaultValues]);

    const filteredData = useMemo(() => {
        if (!data) {
            return [];
        }

        return reduce(
            activeFilters,
            (result, filterValue, filterName) =>
                isFilterEnabled(filterValue)
                    ? result.filter((item) => filters[filterName].filterFunction(item, activeFilters[filterName]))
                    : result,
            data,
        );
    }, [activeFilters, data, filters]);

    return {
        FilterButton,
        FilterBar,
        filteredData,
    };
};

const FilterLabel = ({ label, onRemove, onClick }: { label: string; onRemove: () => void; onClick: () => void }) => {
    return (
        <FilterLabelWrapper $gap={16} $rowCenter onClick={onClick}>
            <Text $themeColor="white">{label}</Text>
            <RemoveIcon
                size="micro"
                onClick={(ev) => {
                    ev.stopPropagation();
                    onRemove();
                }}
            />
        </FilterLabelWrapper>
    );
};

const FilterLabelWrapper = styled(HorizontalLayout)`
    padding: 6px 16px;
    border-radius: 3px;
    background-color: ${({ theme }) => theme.colors.cocoa900};
    color: white;
`;

const RemoveIcon = styled(IconClose)`
    cursor: pointer;
`;

const TableFilter = ({
    children,
    activeFilterCount,
    openModal,
    closeModal,
    filterModalOpen,
    filters,
    onSubmit,
    onClear,
}: PropsWithChildren<{
    activeFilterCount: number;
    openModal: () => void;
    closeModal: () => void;
    filterModalOpen: boolean;
    filters: any;
    onSubmit: (formValue: any) => void;
    onClear: () => void;
}>) => {
    return (
        <>
            <Button onClick={openModal} variant="outline2" size="sm">
                <LuFilter />
                Filter
                {activeFilterCount > 0 && (
                    <FilterBadge>
                        <Text $themeColor="white" helper>
                            {activeFilterCount}
                        </Text>
                    </FilterBadge>
                )}
            </Button>
            {filterModalOpen && (
                <Modal onClose={closeModal} modalTitle="Filter" allowOverflow>
                    <Form defaultValues={filters} onSubmit={onSubmit}>
                        <ModalContent>{children}</ModalContent>
                        <ButtonsContainer $gap={16}>
                            <Button variant="outline2" onClick={onClear}>
                                Clear all
                            </Button>
                            <Button type="submit">Show</Button>
                        </ButtonsContainer>
                    </Form>
                </Modal>
            )}
        </>
    );
};

const FilterBadge = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;

    border-radius: 32px;
    width: 18px;
    height: 18px;

    position: absolute;
    top: -4px;
    right: -4px;

    background-color: ${({ theme }) => theme.colors.cocoa900};
`;

const isFilterEnabled = (filterValue: FilterValue) =>
    isObjectLike(filterValue) ? !isEmpty(filterValue) : !!filterValue;
